import React from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CommentIcon from '@mui/icons-material/Comment';
import { useTranslation } from 'react-i18next';
import NoteDialog from './NoteDialog';
import ConfirmationDialog from '../../ConfirmationDialog';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { useNoteCategory } from '../../../hooks/useNoteCategory';
import {
  BaseNote,
  ProviderNote,
  ProviderNoteComment,
} from '../../../data/models/provider-note.model';
import * as providerSlice from '../../../data/provider.slice';
import * as referenceSlice from '../../../data/reference.slice';
import NoteCommentDialog from './NoteCommentDialog';
import { useCanEditNote } from '../../../hooks/permissions/useCanEditNote';
import { useCanCreateNote } from '../../../hooks/permissions/useCanCreateNote';
import { useCanDeleteNote } from '../../../hooks/permissions/useCanDeleteNote';

const PREFIX = 'Notes';

const classes = {
  container: `${PREFIX}-container`,
  toolbar: `${PREFIX}-toolbar`,
  grid: `${PREFIX}-grid`,
  note: `${PREFIX}-note`,
  comment: `${PREFIX}-comment`,
  col1: `${PREFIX}-col1`,
  col2: `${PREFIX}-col2`,
  col3: `${PREFIX}-col3`,
  col4: `${PREFIX}-col4`,
  col5: `${PREFIX}-col5`,
  col6: `${PREFIX}-col6`,
  hide: `${PREFIX}-hide`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },

  [`& .${classes.toolbar}`]: {
    display: 'flex',
    justifyContent: 'right',
    marginBottom: theme.spacing(3),
  },

  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },

  [`& .${classes.note}`]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  [`& .${classes.comment}`]: {
    backgroundColor: '#eee',
  },

  [`& .${classes.col1}`]: { width: 50 },
  [`& .${classes.col2}`]: { width: 65 },
  [`& .${classes.col3}`]: {
    width: 100,
    maxWidth: 100,
  },
  [`& .${classes.col4}`]: { width: 100 },
  [`& .${classes.col5}`]: {},
  [`& .${classes.col6}`]: { width: 150 },
  [`& .${classes.hide}`]: { visibility: 'hidden' },
}));

const RenderDate = ({ value }: { value?: string }) => {
  return <>{useFormatDate(value)}</>;
};

const RenderName = ({ note }: { note: BaseNote }) => {
  const fullName = `${note?.userAccount?.userFirstName || ''} ${
    note?.userAccount?.userLastName || ''
  }`.trim();
  return (
    <Tooltip title={fullName}>
      <span>{fullName || '-'}</span>
    </Tooltip>
  );
};

const RenderCategory = ({ categoryId }: { categoryId: number }) => {
  return <>{useNoteCategory(categoryId)}</>;
};

interface CommentRowProps {
  key: React.Key | undefined;
  comment: ProviderNoteComment;
  editComment: Function;
  deleteComment: Function;
}

const CommentRow = ({
  comment,
  editComment,
  deleteComment,
}: CommentRowProps) => {
  const { t } = useTranslation('common');
  const canEditNote = useCanEditNote(comment);
  const canDeleteNote = useCanDeleteNote(comment);

  const editTitle = t('notes.editComment');
  const deleteTitle = t('notes.deleteComment');

  return (
    <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
      <TableCell className={classes.col1}>&nbsp;</TableCell>
      <TableCell className={classes.col2}>
        <RenderDate value={comment.createdOnUTC} />
      </TableCell>
      <TableCell className={classes.col3}>
        <RenderName note={comment} />
      </TableCell>
      <TableCell className={classes.col4}>&nbsp;</TableCell>
      <TableCell className={classes.col5}>{comment.note}</TableCell>
      <TableCell className={classes.col6}>
        <Tooltip title={editTitle}>
          <IconButton
            data-testid="edit"
            disabled={!canEditNote}
            onClick={() => editComment(comment)}
            color="default"
            size="large">
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={deleteTitle}>
          <IconButton
            data-testid="delete"
            disabled={!canDeleteNote}
            onClick={(evt) =>
              deleteComment(
                evt,
                comment.transportationProviderNoteID,
                comment.transportationProviderNoteCommentID,
              )
            }
            color="primary"
            size="large">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

interface NoteRowProps {
  key: React.Key | undefined;
  note: ProviderNote;
  editNote: Function;
  deleteNote: Function;
  createComment: Function;
  editComment: Function;
  deleteComment: Function;
}

const NoteRow = ({
  note,
  editNote,
  deleteNote,
  createComment,
  editComment,
  deleteComment,
}: NoteRowProps) => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const [open, setOpen] = React.useState(false);

  const addCommentTitle = t('notes.addComment');
  const editTitle = t('notes.editNote');
  const deleteTitle = t('notes.deleteNote');

  const toggleNote = () => {
    if (!open) {
      dispatch(
        providerSlice.fetchNoteCommentsAsync(
          note.transportationProviderID,
          note.transportationProviderNoteID!,
        ),
      );
    }
    setOpen(!open);
  };

  const canEditNote = useCanEditNote(note);
  const canCreateNote = useCanCreateNote();
  const canDeleteNote = useCanDeleteNote(note);

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell className={classes.col1}>
          <IconButton
            aria-label="expand row"
            size="small"
            className={!note.commentCount ? classes.hide : ''}
            onClick={toggleNote}>
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell className={classes.col2}>
          <RenderDate value={note.createdOnUTC} />
        </TableCell>
        <TableCell className={classes.col3}>
          <RenderName note={note} />
        </TableCell>
        <TableCell className={classes.col4}>
          <RenderCategory categoryId={note.providerNoteCategoryID} />
        </TableCell>
        <TableCell className={classes.col5}>{note.note}</TableCell>
        <TableCell className={classes.col6}>
          <Tooltip title={editTitle}>
            <IconButton
              data-testid="edit"
              disabled={!canEditNote}
              onClick={() => editNote(note)}
              color="default"
              size="large">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={addCommentTitle}>
            <IconButton
              data-testid="comment"
              disabled={!canCreateNote}
              onClick={() => createComment(note)}
              color="default"
              size="large">
              <CommentIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={deleteTitle}>
            <IconButton
              data-testid="delete"
              disabled={!canDeleteNote}
              onClick={(evt) =>
                deleteNote(evt, note.transportationProviderNoteID)
              }
              color="primary"
              size="large">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={6}>
          <Collapse
            style={{ width: '100%' }}
            in={open}
            timeout="auto"
            unmountOnExit>
            <Table size="small" className={classes.comment}>
              <TableBody>
                {note.transportationProviderNoteComments?.map((c) => (
                  <CommentRow
                    key={c.transportationProviderNoteCommentID}
                    comment={c}
                    editComment={editComment}
                    deleteComment={deleteComment}
                  />
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

interface NotesProps {
  providerId: number;
}

export default function Notes({ providerId }: NotesProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const canCreateNote = useCanCreateNote();

  const [currentNote, setCurrentNote] = React.useState<ProviderNote>();
  const [noteEditorOpen, setNoteEditorOpen] = React.useState<boolean>(false);
  const [currentComment, setCurrentComment] =
    React.useState<ProviderNoteComment>();
  const [commentEditorOpen, setCommentEditorOpen] =
    React.useState<boolean>(false);

  const [confirmDeleteNoteOpen, setConfirmDeleteNoteOpen] =
    React.useState(false);
  const [confirmDeleteCommentOpen, setConfirmDeleteCommentOpen] =
    React.useState(false);
  const [confirmMessage, setConfirmMessage] = React.useState('');
  const [deleteNoteId, setDeleteNoteId] = React.useState(0);
  const [deleteCommentId, setDeleteCommentId] = React.useState(0);

  const providerNotes = useAppSelector(providerSlice.selectProviderNotes);
  const providerNoteCategories = useAppSelector(
    referenceSlice.selectProviderNoteCategories,
  );

  React.useEffect(() => {
    if (providerId) {
      dispatch(providerSlice.fetchProviderNotesAsync(providerId));
    }
    dispatch(referenceSlice.fetchProviderNoteCategoriesAsync());
  }, [providerId, dispatch]);

  const createNote = () => {
    setCurrentNote(undefined);
    setNoteEditorOpen(true);
  };

  const editNote = (note: ProviderNote) => {
    setCurrentNote(note);
    setNoteEditorOpen(true);
  };

  const deleteNote = (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    noteId: number,
  ) => {
    evt.preventDefault();
    setDeleteNoteId(noteId);
    setConfirmMessage(t('provider.confirmDeleteNote') || '');
    setConfirmDeleteNoteOpen(true);
  };

  const confirmDeleteNote = () => {
    setConfirmDeleteNoteOpen(false);
    dispatch(providerSlice.deleteNoteAsync(providerId, deleteNoteId));
  };

  const cancelDeleteNote = () => {
    setConfirmDeleteNoteOpen(false);
  };

  const closeNoteEditor = (saved: boolean) => {
    setNoteEditorOpen(false);
    setCurrentNote(undefined);
  };

  const closeCommentEditor = (saved: boolean) => {
    setCommentEditorOpen(false);
    setCurrentNote(undefined);
    setCurrentComment(undefined);
  };

  const createComment = (note: ProviderNote) => {
    setCurrentNote(note);
    setCurrentComment(undefined);
    setCommentEditorOpen(true);
  };

  const editComment = (comment: ProviderNoteComment) => {
    setCurrentComment(comment);
    setCommentEditorOpen(true);
  };

  const deleteComment = (evt: any, noteId: number, commentId: number) => {
    evt.preventDefault();
    setDeleteNoteId(noteId);
    setDeleteCommentId(commentId);
    setConfirmMessage(t('provider.confirmDeleteComment') || '');
    setConfirmDeleteCommentOpen(true);
  };

  const confirmDeleteComment = () => {
    setConfirmDeleteCommentOpen(false);
    dispatch(
      providerSlice.deleteNoteCommentAsync(
        providerId,
        deleteNoteId,
        deleteCommentId,
      ),
    );
  };

  const cancelDeleteComment = () => {
    setConfirmDeleteCommentOpen(false);
  };

  return (
    <StyledBox className={classes.container}>
      {canCreateNote ? (
        <Box className={classes.toolbar}>
          <Button variant="contained" color="primary" onClick={createNote}>
            {t('provider.addNote')}
          </Button>
        </Box>
      ) : null}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>&nbsp;</TableCell>
              <TableCell>{t('provider.noteDate')}</TableCell>
              <TableCell>{t('provider.noteName')}</TableCell>
              <TableCell>{t('provider.noteCategory')}</TableCell>
              <TableCell>{t('provider.note')}</TableCell>
              <TableCell>&nbsp;</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {providerNotes.map((note) => (
              <NoteRow
                key={note.transportationProviderNoteID}
                note={note}
                editNote={editNote}
                deleteNote={deleteNote}
                createComment={createComment}
                editComment={editComment}
                deleteComment={deleteComment}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <NoteDialog
        providerId={providerId}
        title={currentNote ? t('provider.editNote') : t('provider.addNote')}
        note={currentNote}
        open={noteEditorOpen}
        close={closeNoteEditor}
        providerNoteCategories={providerNoteCategories}
      />
      <NoteCommentDialog
        providerId={providerId}
        title={
          currentComment ? t('provider.editComment') : t('provider.addComment')
        }
        noteId={currentNote?.transportationProviderNoteID || 0}
        comment={currentComment}
        open={commentEditorOpen}
        close={closeCommentEditor}
      />
      <ConfirmationDialog
        title={t('common.confirmDelete')}
        message={confirmMessage}
        open={confirmDeleteNoteOpen}
        positiveAction={confirmDeleteNote}
        negativeAction={cancelDeleteNote}
      />
      <ConfirmationDialog
        title={t('common.confirmDelete')}
        message={confirmMessage}
        open={confirmDeleteCommentOpen}
        positiveAction={confirmDeleteComment}
        negativeAction={cancelDeleteComment}
      />
    </StyledBox>
  );
}
